import React, { useEffect, useState } from 'react';
import * as userService from '../services/userService';
import { Link } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';

interface UserData {
  _id: string;
  name: string;
  email: string;
  credit: number;
  subscription: {
    planName: string;
    startDate: string;
    expiresAt: string;
    status: string;
    purchaseMonth?: string;
  };
}

const AccountStatus: React.FC = () => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        const response = await userService.getCurrentUser();
        if (response.success) {
          setUserData(response.data);
        } else {
          setError('خطا در دریافت اطلاعات کاربری');
        }
      } catch (err) {
        setError('خطا در برقراری ارتباط با سرور');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  // Format date to Persian style (YYYY/MM/DD)
  const formatDate = (dateString: string) => {
    if (!dateString) return 'نامشخص';
    const date = new Date(dateString);
    return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(2, '0')}/${String(date.getDate()).padStart(2, '0')}`;
  };

  // Convert purchase month (YYYY-MM) to Persian month name
  const getPersianMonthName = (monthString?: string) => {
    if (!monthString) return 'نامشخص';
    
    const [year, month] = monthString.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    
    const persianMonths = [
      'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
      'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند'
    ];
    
    return `${persianMonths[monthIndex]} ${year}`;
  };

  // Calculate remaining days for subscription
  const getRemainingDays = (expiryDate: string) => {
    if (!expiryDate) return 0;
    
    const expiry = new Date(expiryDate);
    const today = new Date();
    
    // Reset time part for accurate day calculation
    expiry.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays > 0 ? diffDays : 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <div className="p-6 bg-white rounded-lg shadow-md w-full max-w-md">
          <h2 className="text-xl font-bold text-red-500 mb-4 text-center">خطا</h2>
          <p className="text-slate-700 text-center">{error}</p>
          <div className="mt-6 text-center">
            <Link to="/" className="text-indigo-500 hover:text-indigo-700">
              بازگشت به صفحه اصلی
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const isSubscriptionActive = userData?.subscription?.expiresAt && 
    new Date(userData.subscription.expiresAt).getTime() > new Date().getTime();

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8 border-b border-gray-200 pb-5">
          <h1 className="text-2xl font-bold text-gray-900">وضعیت حساب کاربری</h1>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-center">{error}</p>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Current Subscription Card */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">اشتراک فعلی</h2>
                
                {isSubscriptionActive ? (
                  <div className="flex flex-col md:flex-row md:items-center justify-between">
                    <div className="mb-4 md:mb-0">
                      <div className="bg-indigo-50 py-1 px-3 rounded-full inline-block">
                        <span className="text-indigo-700 font-medium text-sm">
                          {userData?.subscription?.planName || 'رایگان'}
                        </span>
                      </div>
                      <div className="mt-2 text-gray-600">
                        <span className="font-semibold">ماه خرید:</span> {getPersianMonthName(userData?.subscription?.purchaseMonth)}
                      </div>
                      <div className="mt-1 text-gray-600">
                        <span className="font-semibold">تاریخ انقضا:</span> {formatDate(userData?.subscription?.expiresAt || '')}
                      </div>
                    </div>

                    <div className="flex flex-col items-end">
                      <div className="text-3xl font-bold text-indigo-600 flex items-center">
                        {getRemainingDays(userData?.subscription?.expiresAt || '')}
                        <span className="text-gray-500 text-lg mr-1">روز</span>
                      </div>
                      <span className="text-gray-500 text-sm">باقی مانده از اشتراک</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <p className="text-gray-600 mb-4">شما در حال حاضر اشتراک فعالی ندارید.</p>
                    <Link 
                      to="/upgrade" 
                      className="bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-6 rounded-md transition duration-200 inline-block"
                    >
                      خرید اشتراک
                    </Link>
                  </div>
                )}
              </div>

              {isSubscriptionActive && (
                <div className="bg-gray-50 p-4 flex justify-between items-center">
                  <span className="text-gray-600">ارتقا یا تمدید اشتراک</span>
                  <Link 
                    to="/upgrade" 
                    className="text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    مشاهده طرح‌ها
                  </Link>
                </div>
              )}
            </div>

            {/* Credit Card */}
            <div className="bg-white shadow-md rounded-xl overflow-hidden">
              <div className="p-6 border-b border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">اعتبار حساب</h2>
                
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div className="mb-4 md:mb-0">
                    <div className="text-gray-600">
                      <span className="font-semibold">اعتبار فعلی شما:</span>
                    </div>
                  </div>

                  <div className="flex flex-col items-end">
                    <div className="text-3xl font-bold text-indigo-600 flex items-center">
                      {userData?.credit || 0}
                      <span className="text-gray-500 text-lg mr-1">اعتبار</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 flex justify-between items-center">
                <span className="text-gray-600">افزایش اعتبار حساب</span>
                <Link 
                  to="/charge-credit" 
                  className="text-indigo-600 hover:text-indigo-700 font-medium"
                >
                  شارژ اعتبار
                </Link>
              </div>
            </div>

            {/* Transactions Link */}
            <div className="flex justify-center">
              <Link 
                to="/transactions" 
                className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-1" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z" clipRule="evenodd" />
                </svg>
                مشاهده تمام تراکنش‌ها
              </Link>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AccountStatus;
